import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Divider,
  List,
  ListItem,
  ListItemText,
  TextField,
  IconButton,
  Chip,
  Alert,
  Paper,
  Checkbox,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  CircularProgress
} from '@mui/material';
import {
  CreditCard,
  AttachMoney,
  Group,
  Receipt,
  Check,
  Close,
  Remove,
  Add,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useStore } from '../store/useStore';
import { Order, OrderItem } from '../types';
import { useNavigate } from 'react-router-dom';
import { setPaymentCallbacks, clearPaymentCallbacks } from '../interfaces/BtInterfaces';
import { useLanguage } from '../contexts/LanguageContext';

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order | undefined;
  tableId: string;
}

interface SplitItem {
  item: OrderItem;
  quantity: number;
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({ open, onClose, order, tableId }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { processPayment } = useStore();
  const { t } = useLanguage();
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card'>('card');
  const [activeStep, setActiveStep] = useState(0);
  const [selectedItems, setSelectedItems] = useState<SplitItem[]>([]);
  const [processing, setProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [paymentError, setPaymentError] = useState<string>('');
  const [paymentStatus, setPaymentStatus] = useState<string>('');
  const [tipPercentage, setTipPercentage] = useState<number>(0);
  const isLandscape = window.matchMedia('(orientation: landscape)').matches;
  const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1024;
  const isMobileLandscape = isLandscape && window.innerWidth < 768;
  const isTabletLandscape = isLandscape && isTablet;
  const isAnyLandscape = isMobileLandscape || isTabletLandscape;

  // Initialize with all items selected
  React.useEffect(() => {
    if (open && selectedItems.length === 0 && order && order.items.length > 0) {
      setSelectedItems(order.items.map(item => ({ item, quantity: item.quantity })));
    }
  }, [open, order, selectedItems.length]);

  // Set up payment callbacks when processing starts
  useEffect(() => {
    if (processing) {
      setPaymentCallbacks(
        (status: string) => {
          // Update payment status message
          setPaymentStatus(status);
        },
        (code: number, msg: string) => {
          // Handle payment completion
          if (code === 0) {
            // Payment successful
            handlePaymentSuccess();
          } else {
            // Payment failed
            setPaymentFailed(true);
            setPaymentError(msg || 'Payment failed');
            setProcessing(false);
          }
        }
      );
    }

    // Cleanup callbacks when component unmounts or processing stops
    return () => {
      if (!processing) {
        clearPaymentCallbacks();
      }
    };
  }, [processing]);

  if (!order) return null;

  const handleItemSelection = (item: OrderItem, quantity: number) => {
    setSelectedItems(prev => {
      const existing = prev.find(si => si.item.id === item.id);
      if (existing) {
        if (quantity === 0) {
          return prev.filter(si => si.item.id !== item.id);
        }
        return prev.map(si => 
          si.item.id === item.id ? { ...si, quantity } : si
        );
      } else if (quantity > 0) {
        return [...prev, { item, quantity }];
      }
      return prev;
    });
  };

  const getSelectedQuantity = (itemId: string) => {
    const item = selectedItems.find(si => si.item.id === itemId);
    return item?.quantity || 0;
  };

  const selectedTotal = selectedItems.reduce((sum, si) => 
    sum + (si.item.price * si.quantity), 0
  );

  const subtotal = selectedTotal > 0 ? selectedTotal : order.total;
  const tipAmount = subtotal * (tipPercentage / 100);
  const totalWithTip = subtotal + tipAmount;

  const handlePaymentSuccess = () => {
    // Check if this is a partial payment
    const isFullPayment = selectedItems.length === order.items.length && 
      selectedItems.every(si => {
        const orderItem = order.items.find(oi => oi.id === si.item.id);
        return orderItem && si.quantity === orderItem.quantity;
      });
    
    if (!isFullPayment && selectedItems.length > 0) {
      // For partial payment, pass the items and quantities that were paid
      processPayment(order.id, paymentMethod, selectedItems);
    } else if (isFullPayment) {
      // For full payment, don't pass items (will remove entire order)
      processPayment(order.id, paymentMethod);
    }
    
    setProcessing(false);
    setPaymentComplete(true);
    
    // Navigate back after payment
    setTimeout(() => {
      onClose();
      navigate('/');
    }, 2000);
  };

  const handlePayment = async () => {
    setProcessing(true);
    setPaymentStatus('Initializing payment...');
    setPaymentFailed(false);
    setPaymentError('');
    
    // Trigger BtDevice payment with tip included
    if (window.BtDevice) {
      try {
        if (paymentMethod === 'card') {
          window.BtDevice.startPayment(totalWithTip);
        } else {
          window.BtDevice.cashPayment(totalWithTip);
          // For cash payment, we might want to complete immediately
          // unless there's a cash register integration
          setTimeout(() => {
            if (window.BtApp?.paymentFinished) {
              window.BtApp.paymentFinished(0, 'Cash payment recorded');
            }
          }, 1000);
        }
      } catch (error) {
        console.error('Payment device error:', error);
        setPaymentFailed(true);
        setPaymentError('Failed to initialize payment device');
        setProcessing(false);
      }
    } else {
      // If BtDevice is not available, simulate for development
      console.warn('BtDevice not available, simulating payment...');
      setTimeout(() => {
        if (window.BtApp?.paymentStatus) {
          window.BtApp.paymentStatus('Processing payment...');
        }
        setTimeout(() => {
          if (window.BtApp?.paymentFinished) {
            // Simulate success for testing
            window.BtApp.paymentFinished(0, 'Payment successful');
          }
        }, 2000);
      }, 1000);
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSelectAll = () => {
    if (selectedItems.length === order.items.length &&
        selectedItems.every((si, idx) => si.quantity === order.items[idx].quantity)) {
      setSelectedItems([]);
    } else {
      setSelectedItems(order.items.map(item => ({ item, quantity: item.quantity })));
    }
  };

  // Payment success modal
  if (paymentComplete) {
    return (
      <Dialog open={open} maxWidth="sm" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 5 }}>
          <Box sx={{ 
            width: 80, 
            height: 80, 
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3
          }}>
            <Check sx={{ fontSize: 48, color: 'white' }} />
          </Box>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            {t('payment.paymentSuccessful')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('payment.transactionCompleted', { amount: totalWithTip.toFixed(2) })}
            {tipPercentage > 0 && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                ({t('common.tip')}: €{tipAmount.toFixed(2)})
              </Typography>
            )}
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  // Payment failed modal
  if (paymentFailed) {
    return (
      <Dialog open={open} maxWidth="sm" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 5 }}>
          <Box sx={{ 
            width: 80, 
            height: 80, 
            borderRadius: '50%',
            backgroundColor: '#f44336',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3
          }}>
            <ErrorIcon sx={{ fontSize: 48, color: 'white' }} />
          </Box>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Payment Failed
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {paymentError}
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              setPaymentFailed(false);
              setPaymentError('');
              setActiveStep(1); // Go back to payment method selection
            }}
            sx={{ mt: 3 }}
          >
            Try Again
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  // Payment processing modal
  if (processing) {
    return (
      <Dialog open={open} maxWidth="sm" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 5 }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Processing Payment
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {paymentStatus || 'Please wait...'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Amount: €{totalWithTip.toFixed(2)}
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  const steps = [t('payment.selectItems'), t('payment.paymentMethod'), t('common.confirm')];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={isLandscape && isTablet ? "lg" : "md"} 
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: isAnyLandscape ? '90vh' : '70vh',
          maxWidth: isLandscape && isTablet ? '1000px' : '900px',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: isAnyLandscape ? 0.5 : 2,
        px: isAnyLandscape ? 1.5 : 3,
        minHeight: isAnyLandscape ? 40 : 'auto'
      }}>
        <Typography variant={isAnyLandscape ? 'body2' : 'h6'} sx={{ fontWeight: isAnyLandscape ? 600 : 400 }}>
          Process Payment
        </Typography>
        <IconButton onClick={onClose} sx={{ color: '#1a1a1a', p: isAnyLandscape ? 0.25 : 1 }}>
          <Close fontSize={isAnyLandscape ? 'small' : 'medium'} />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ 
        mt: isAnyLandscape ? 1 : 3, 
        py: isAnyLandscape ? 1 : 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <Stepper activeStep={activeStep} sx={{ mb: isAnyLandscape ? 1 : 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {t('payment.selectItems')}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSelectAll}
                startIcon={selectedItems.length === order.items.length ? <Close /> : <Check />}
              >
                {selectedItems.length === order.items.length ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>
            
            <Paper sx={{ 
              p: isAnyLandscape ? 1 : 2, 
              bgcolor: '#f5f5f5',
              flex: 1,
              overflow: 'auto',
              minHeight: 0
            }}>
              <List>
                {order.items.map((item) => {
                  const selectedQty = getSelectedQuantity(item.id);
                  const remainingQty = item.quantity - selectedQty;
                  
                  return (
                    <ListItem
                      key={item.id}
                      sx={{
                        mb: 1,
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: selectedQty > 0 ? '2px solid #667eea' : '1px solid #e0e0e0'
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {item.name}
                            </Typography>
                            {item.quantity > 1 && (
                              <Chip 
                                label={`${t('common.total')}: ${item.quantity}`} 
                                size="small" 
                                color="default"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary">
                              €{item.price.toFixed(2)} each
                            </Typography>
                            {item.notes && (
                              <Typography variant="caption" color="text.secondary">
                                Note: {item.notes}
                              </Typography>
                            )}
                          </>
                        }
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {item.quantity > 1 ? (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleItemSelection(item, Math.max(0, selectedQty - 1))}
                              disabled={selectedQty === 0}
                            >
                              <Remove />
                            </IconButton>
                            <Box sx={{ minWidth: 100, textAlign: 'center' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {selectedQty} of {item.quantity}
                              </Typography>
                              <Typography variant="caption" color="primary">
                                €{(item.price * selectedQty).toFixed(2)}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => handleItemSelection(item, Math.min(item.quantity, selectedQty + 1))}
                              disabled={selectedQty === item.quantity}
                            >
                              <Add />
                            </IconButton>
                          </>
                        ) : (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedQty === 1}
                                onChange={(e) => handleItemSelection(item, e.target.checked ? 1 : 0)}
                              />
                            }
                            label={`€${item.price.toFixed(2)}`}
                          />
                        )}
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            </Paper>

            {selectedItems.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Selected: {selectedItems.reduce((sum, si) => sum + si.quantity, 0)} items 
                • {t('common.total')}: €{selectedTotal.toFixed(2)}
              </Alert>
            )}
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              Select Payment Method
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: paymentMethod === 'card' ? '2px solid #667eea' : '1px solid #e0e0e0',
                    transition: 'all 0.3s'
                  }}
                  onClick={() => setPaymentMethod('card')}
                >
                  <CardActionArea>
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <CreditCard sx={{ fontSize: 48, color: '#667eea', mb: 1 }} />
                      <Typography variant="h6">{t('payment.card')}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Credit or Debit
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
              <Grid size={6}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: paymentMethod === 'cash' ? '2px solid #667eea' : '1px solid #e0e0e0',
                    transition: 'all 0.3s'
                  }}
                  onClick={() => setPaymentMethod('cash')}
                >
                  <CardActionArea>
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <AttachMoney sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} />
                      <Typography variant="h6">{t('payment.cash')}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pay with cash
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {activeStep === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <Receipt sx={{ mr: 1 }} />
                Payment Summary
              </Typography>
              <List dense>
                {(selectedItems.length > 0 ? selectedItems : order.items.map(item => ({ item, quantity: item.quantity }))).map(({ item, quantity }) => (
                  <ListItem key={item.id}>
                    <ListItemText
                      primary={`${item.name} x${quantity}`}
                      secondary={item.notes}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      €{(item.price * quantity).toFixed(2)}
                    </Typography>
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1">{t('common.subtotal')}:</Typography>
                <Typography variant="body1">
                  €{subtotal.toFixed(2)}
                </Typography>
              </Box>
              
              {/* Tip Selection */}
              <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('payment.addTip')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="contained"
                    size="medium"
                    onClick={() => setTipPercentage(0)}
                    sx={{
                      flex: 1,
                      backgroundColor: tipPercentage === 0 ? '#424242' : '#e0e0e0',
                      color: tipPercentage === 0 ? 'white' : '#424242',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: tipPercentage === 0 ? '#303030' : '#d0d0d0'
                      }
                    }}
                  >
                    {t('payment.noTip')}
                  </Button>
                  <Button
                    variant="contained"
                    size="medium"
                    onClick={() => setTipPercentage(5)}
                    sx={{
                      flex: 1,
                      backgroundColor: tipPercentage === 5 ? '#424242' : '#e0e0e0',
                      color: tipPercentage === 5 ? 'white' : '#424242',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: tipPercentage === 5 ? '#303030' : '#d0d0d0'
                      }
                    }}
                  >
                    5%
                  </Button>
                  <Button
                    variant="contained"
                    size="medium"
                    onClick={() => setTipPercentage(10)}
                    sx={{
                      flex: 1,
                      backgroundColor: tipPercentage === 10 ? '#424242' : '#e0e0e0',
                      color: tipPercentage === 10 ? 'white' : '#424242',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: tipPercentage === 10 ? '#303030' : '#d0d0d0'
                      }
                    }}
                  >
                    10%
                  </Button>
                  <Button
                    variant="contained"
                    size="medium"
                    onClick={() => setTipPercentage(15)}
                    sx={{
                      flex: 1,
                      backgroundColor: tipPercentage === 15 ? '#424242' : '#e0e0e0',
                      color: tipPercentage === 15 ? 'white' : '#424242',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: tipPercentage === 15 ? '#303030' : '#d0d0d0'
                      }
                    }}
                  >
                    15%
                  </Button>
                </Box>
                {tipPercentage > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2">{t('payment.tipPercentage', { percentage: tipPercentage })}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, ml: 1 }}>
                      €{tipAmount.toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Box>
              
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">{t('common.total')}:</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#667eea' }}>
                  €{totalWithTip.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('payment.paymentMethod')}: <strong>{paymentMethod === 'card' ? t('payment.card') : t('payment.cash')}</strong>
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: isAnyLandscape ? 1 : 3, minHeight: isAnyLandscape ? 48 : 'auto' }}>
        {activeStep > 0 && (
          <Button 
            onClick={handleBack} 
            size="large"
            variant="contained"
            sx={{
              backgroundColor: '#f0f0f0',
              color: '#424242',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#e0e0e0'
              }
            }}
          >
            Back
          </Button>
        )}
        <Box sx={{ flex: '1 1 auto' }} />
        <Button 
          onClick={onClose} 
          size="large"
          variant="contained"
          sx={{
            backgroundColor: '#e0e0e0',
            color: '#424242',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#d0d0d0'
            }
          }}
        >
          {t('common.cancel')}
        </Button>
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            size="large"
            onClick={handleNext}
            disabled={activeStep === 0 && selectedItems.length === 0}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: '#1a1a1a',
              '&:hover': {
                backgroundColor: '#a3b9a1'
              },
              minWidth: 120
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            size="large"
            onClick={handlePayment}
            disabled={processing}
            sx={{
              backgroundColor: theme.palette.secondary.main,
              '&:hover': {
                backgroundColor: theme.palette.secondary.dark
              },
              minWidth: 200
            }}
          >
            {processing ? t('payment.processing') : paymentMethod === 'card' ? t('payment.chargeCard') : t('payment.chargeCash')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;